import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'

import { ExportarPdf } from '@/app/layout/ExportarPdf'
import { ModuleFrame } from '@/app/layout/ModuleFrame'
import { MODULOS, type Modulo } from '@/app/navigation'
import { useSession } from '@/auth/session'
import { Botao } from '@/components/primitivos'
import { sufixoDoPadrao } from '@/modules/compliance/padrao'
import { SeletorDePadrao } from '@/modules/compliance/SeletorDePadrao'
import { acessoDe } from '@/permissions/resolver'
import { usePermission, usePermissoes } from '@/permissions/usePermission'
import { clienteEmFoco } from '@/tenant/persistencia'

function modulo(id: Modulo['id']): Modulo {
  const encontrado = MODULOS.find((m) => m.id === id)
  if (!encontrado) throw new Error(`Módulo desconhecido: ${id}`)
  return encontrado
}

/**
 * Frame for a client-scoped module: tabs + client selector + `exportar pdf`.
 *
 * 🔒 Tabs filtered by `ver`, like Compliance's. Until 2026-10-14 FinOps and
 * Orçamento passed `m.abas` whole, and the Cliente saw Oportunidades and
 * Configurações. The routes are guarded too (`router.tsx`, `telaDaAba`).
 */
function FrameEscopado({ id }: { id: Modulo['id'] }) {
  const { idCliente } = useParams()
  const permissoes = usePermissoes()
  const m = modulo(id)
  return (
    <ModuleFrame
      titulo={m.rotulo}
      abas={m.abas.filter((aba) => acessoDe(permissoes, aba.permissao).ver)}
      base={`/cliente/${idCliente}/${m.id}`}
      mostrarSeletorCliente
      acoes={<ExportarPdf />}
    />
  )
}

export function FinOpsFrame() {
  return <FrameEscopado id="finops" />
}

export function OrcamentoFrame() {
  return <FrameEscopado id="orcamento" />
}

/**
 * Compliance (handoff v4) — tabs `Perguntas do padrão` · `Questionário` · `Documentos`,
 * the global padrão selector, and the client selector (BR-19).
 *
 * 🔒 Tabs are filtered by `ver`: the Cliente gets TWO tabs, not a disabled third one
 * (README §2, `3a`). `Perguntas do padrão` links out of the client scope, and every
 * tab carries the `?padrao=` selection across the switch.
 */
export function ComplianceFrame() {
  const { idCliente } = useParams()
  const { pathname, search } = useLocation()
  const permissoes = usePermissoes()
  const m = modulo('compliance')
  const base = `/cliente/${idCliente}/compliance`
  const sufixo = sufixoDoPadrao(search)
  const noQuestionario = pathname.endsWith('/questionario')

  return (
    <ModuleFrame
      titulo={m.rotulo}
      abas={m.abas.filter((aba) => acessoDe(permissoes, aba.permissao).ver)}
      base={base}
      destinoDaAba={(aba) =>
        (aba.global ? `/compliance/${aba.segmento}` : `${base}/${aba.segmento}`) + sufixo
      }
      mostrarSeletorCliente
      acoes={
        <>
          <SeletorDePadrao />
          {noQuestionario && (
            // `window.print()` + `@media print` — decidido em 20/08 (CLAUDE.md).
            <Botao variante="secundaria" icone="file-pdf" onClick={() => window.print()}>
              exportar pdf
            </Botao>
          )}
        </>
      }
    />
  )
}

/**
 * `Perguntas do padrão` (`3a`) — global, IOX-only (BR-23), and NO client selector.
 *
 * The other two tabs are client-scoped, so from here they point at the client in
 * focus (`clienteEmFoco`, the same rule the sidebar uses) — or at `/` when the user
 * has none, which lands on the "nenhum cliente atribuído" state.
 */
export function PerguntasDoPadraoFrame() {
  const { sessao } = useSession()
  const { search } = useLocation()
  const [, setParams] = useSearchParams()
  const permissoes = usePermissoes()
  const { editar } = usePermission('compliance.perguntas')
  const m = modulo('compliance')
  const cliente = clienteEmFoco(sessao?.clientes ?? [])
  const sufixo = sufixoDoPadrao(search)

  return (
    <ModuleFrame
      titulo={m.rotulo}
      abas={m.abas.filter((aba) => acessoDe(permissoes, aba.permissao).ver)}
      base="/compliance"
      destinoDaAba={(aba) => {
        if (aba.global) return `/compliance/${aba.segmento}${sufixo}`
        return cliente ? `/cliente/${cliente}/compliance/${aba.segmento}${sufixo}` : '/'
      }}
      mostrarSeletorCliente={false}
      extrasEsquerda={
        <span className="rounded-control bg-navy-100 text-iox-navy px-3 py-1.5 type-label whitespace-nowrap">
          só IOX — aba não aparece para o cliente
        </span>
      }
      acoes={
        <>
          <SeletorDePadrao />
          {editar && (
            <Botao
              variante="accent"
              icone="plus"
              onClick={() =>
                setParams((atuais) => {
                  const proximos = new URLSearchParams(atuais)
                  proximos.set('novo', '1')
                  return proximos
                })
              }
            >
              novo padrão
            </Botao>
          )}
        </>
      }
    />
  )
}

/**
 * Administração — tabs, and NO client selector. Its right-hand slot carries
 * `novo cliente` (shot `2n`), not `exportar pdf`: same bar, different action per
 * module.
 *
 * The button opens the form on `2n` through `?novo=1` — the same URL-driven pattern
 * as `novo padrão` above, so it works from any Administração tab and survives a
 * reload. Only rendered with `admin.clientes` editar; the Lambda re-checks it.
 */
export function AdminFrame() {
  const m = modulo('admin')
  const navigate = useNavigate()
  const permissoes = usePermissoes()
  const { editar } = usePermission('admin.clientes')
  return (
    <ModuleFrame
      titulo={m.rotulo}
      // 🔒 Filtered by `ver` since 2026-10-14 — same rule as the other frames.
      abas={m.abas.filter((aba) => acessoDe(permissoes, aba.permissao).ver)}
      base="/admin"
      mostrarSeletorCliente={false}
      acoes={
        editar ? (
          <Botao variante="accent" onClick={() => void navigate('/admin/clientes?novo=1')}>
            novo cliente
          </Botao>
        ) : undefined
      }
    />
  )
}
